import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminService, EventService, BannerService, StoryService, CertificateService } from '../../services/APIService';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    users: 0,
    events: 0,
    stories: 0,
    banners: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // First attempt to use the comprehensive dashboard endpoint
        try {
          const data = await AdminService.getDashboardData();
          setDashboardData({
            users: data.userCount || 0,
            events: data.eventCount || 0,
            stories: data.storyCount || 0,
            banners: data.bannerCount || 0
          });
          
          if (data.recentActivities) {
            setRecentActivities(data.recentActivities);
          }
        } catch (dashboardError) {
          // If comprehensive endpoint fails, fetch each count + build real recent activity

          const toArr = (r, key) => Array.isArray(r) ? r : (r?.[key] || []);
          const timeAgo = (dateStr) => {
            if (!dateStr) return '';
            const diff = Date.now() - new Date(dateStr).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 60) return `${mins || 1} minute${mins !== 1 ? 's' : ''} ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
            const days = Math.floor(hrs / 24);
            return `${days} day${days !== 1 ? 's' : ''} ago`;
          };

          const [usersRes, eventsRes, storiesRes, bannersRes, certsRes] = await Promise.allSettled([
            AdminService.getAllUsers(),
            EventService.getAllEvents(),
            StoryService.getAllStories(),
            BannerService.getAllBanners(),
            CertificateService.getAll(),
          ]);

          const users   = usersRes.status   === 'fulfilled' ? toArr(usersRes.value,   'users')   : [];
          const events  = eventsRes.status  === 'fulfilled' ? toArr(eventsRes.value,  'events')  : [];
          const stories = storiesRes.status === 'fulfilled' ? toArr(storiesRes.value, 'stories') : [];
          const banners = bannersRes.status === 'fulfilled' ? toArr(bannersRes.value, 'banners') : [];
          const certs   = certsRes.status   === 'fulfilled' ? toArr(certsRes.value,   'data')    : [];

          setDashboardData({
            users: users.length,
            events: events.length,
            stories: stories.length,
            banners: banners.length,
          });

          // Build activity list from real data
          const activityItems = [
            ...users.slice(0, 5).map(u => ({
              user: u.name || u.email || 'User',
              action: 'joined as member',
              target: u.email || '',
              time: timeAgo(u.created_at),
              _ts: new Date(u.created_at || 0).getTime(),
            })),
            ...certs.slice(0, 5).map(c => ({
              user: c.issued_by_name || 'Admin',
              action: 'issued certificate to',
              target: c.recipient_name || '',
              time: timeAgo(c.issue_date || c.created_at),
              _ts: new Date(c.issue_date || c.created_at || 0).getTime(),
            })),
            ...events.slice(0, 3).map(e => ({
              user: 'Admin',
              action: 'created event',
              target: e.title || '',
              time: timeAgo(e.created_at || e.date),
              _ts: new Date(e.created_at || e.date || 0).getTime(),
            })),
            ...stories.slice(0, 3).map(s => ({
              user: 'Admin',
              action: 'added success story',
              target: s.title || '',
              time: timeAgo(s.created_at),
              _ts: new Date(s.created_at || 0).getTime(),
            })),
          ];

          activityItems.sort((a, b) => b._ts - a._ts);
          setRecentActivities(activityItems.slice(0, 8));
        }
      } catch (error) {
        console.error('Dashboard data fetch error:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Stats cards data
  const stats = [
    {
      title: 'Total Users',
      value: dashboardData.users,
      change: '+12.5%',
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      title: 'Active Events',
      value: dashboardData.events,
      change: '+5.2%',
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: 'Success Stories',
      value: dashboardData.stories,
      change: '+8.3%',
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      )
    },
    {
      title: 'Active Banners',
      value: dashboardData.banners,
      change: '+0.0%',
      changeType: 'neutral',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];  // Use API data if available
  const recentActivity = recentActivities;

  const quickActions = [
    { 
      title: 'Create Event', 
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', 
      color: 'bg-purple-100 text-purple-600', 
      link: '/admin/events' 
    },
    { 
      title: 'Add Success Story', 
      icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z', 
      color: 'bg-amber-100 text-amber-600', 
      link: '/admin/success-stories' 
    },
    { 
      title: 'Manage Banners', 
      icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', 
      color: 'bg-emerald-100 text-emerald-600', 
      link: '/admin/banners' 
    },
    { 
      title: 'View Users', 
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', 
      color: 'bg-blue-100 text-blue-600', 
      link: '/admin/users' 
    }
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome to Karigar Foundation admin panel
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-3 w-3 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-sm font-medium text-gray-500">
            System Status: <span className="text-green-600">Active</span>
          </span>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, index) => (
          <div 
            key={index}
            className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-full ${
                stat.title === 'Total Users' ? 'bg-blue-100 text-blue-600' : 
                stat.title === 'Active Events' ? 'bg-purple-100 text-purple-600' :
                stat.title === 'Success Stories' ? 'bg-amber-100 text-amber-600' :
                'bg-emerald-100 text-emerald-600'
              }`}>
                {stat.icon}
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <span className={`text-xs font-medium ${
                stat.changeType === 'positive' ? 'text-green-600' : 
                stat.changeType === 'negative' ? 'text-red-600' : 
                'text-gray-600'
              }`}>
                {stat.change}
              </span>
              <span className="text-xs text-gray-500 ml-2">from last month</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
          <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            View All
          </button>
        </div>
        <div className="divide-y divide-gray-200">
          {recentActivity.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-400">No recent activity yet.</div>
          ) : recentActivity.map((item, index) => (
            <div key={index} className="px-6 py-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-sm">
                    {(item.user || '?').charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{item.user}</span>
                    {' '}{item.action}{' '}
                    {item.target && <span className="font-medium text-indigo-600">{item.target}</span>}
                  </p>
                  <p className="text-xs text-gray-400">{item.time}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link 
              key={index}
              to={action.link}
              className="flex items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className={`p-2 rounded-lg ${action.color}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
                </svg>
              </div>
              <span className="ml-3 font-medium">{action.title}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
